import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { usersService, User, CreateUserData, UpdateUserData } from '@/services/users.service';
import RoleSelector from './RoleSelector';
import toast from 'react-hot-toast';

interface UserModalProps {
  user?: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UserModal({ user, isOpen, onClose, onSuccess }: UserModalProps) {
  const isEditing = !!user;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    isActive: true,
    emailVerified: false,
    roleIds: [] as string[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when user changes or modal opens
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone ?? '',
        isActive: user.isActive,
        emailVerified: user.emailVerified,
        roleIds: user.roles.map(r => r.id),
      });
    } else {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        isActive: true,
        emailVerified: false,
        roleIds: [],
      });
    }
    setErrors({});
  }, [user, isOpen]);

  // Create/Update mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateUserData) => usersService.createUser(data),
    onSuccess: () => {
      onSuccess();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message ?? 'Failed to create user');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; updates: UpdateUserData }) =>
      usersService.updateUser(data.id, data.updates),
    onSuccess: () => {
      onSuccess();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message ?? 'Failed to update user');
    },
  });

  // Update roles mutation
  const updateRolesMutation = useMutation({
    mutationFn: async ({ userId, roleIds }: { userId: string; roleIds: string[] }) => {
      // Get current roles
      const currentRoleIds = user?.roles.map(r => r.id) ?? [];

      // Find roles to add and remove
      const rolesToAdd = roleIds.filter(id => !currentRoleIds.includes(id));
      const rolesToRemove = currentRoleIds.filter(id => !roleIds.includes(id));

      // Add new roles
      for (const roleId of rolesToAdd) {
        await usersService.assignRole(userId, roleId);
      }

      // Remove old roles
      for (const roleId of rolesToRemove) {
        await usersService.removeRole(userId, roleId);
      }
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isEditing && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) { return; }

    try {
      if (isEditing) {
        // Update user
        const updates: UpdateUserData = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone ?? null,
          isActive: formData.isActive,
          emailVerified: formData.emailVerified,
        };

        await updateMutation.mutateAsync({ id: user.id, updates });

        // Update roles if changed
        const currentRoleIds = user.roles.map(r => r.id).sort();
        const newRoleIds = formData.roleIds.sort();
        if (JSON.stringify(currentRoleIds) !== JSON.stringify(newRoleIds)) {
          await updateRolesMutation.mutateAsync({
            userId: user.id,
            roleIds: formData.roleIds,
          });
        }
      } else {
        // Create user
        const createData: CreateUserData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone ?? undefined,
          roleIds: formData.roleIds,
          isActive: formData.isActive,
        };

        await createMutation.mutateAsync(createData);
      }
    } catch (_error) {
      // Error handling is done in mutation onError
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || updateRolesMutation.isPending;

  if (!isOpen) { return null; }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex items-start justify-center min-h-screen p-4 pt-16">
        <div className="relative bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-2xl w-full mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
              {isEditing ? 'Edit User' : 'Create User'}
            </h2>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Email */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={errors.email ? 'border-danger-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.email}</p>
                )}
              </div>

              {/* Password (only for create) */}
              {!isEditing && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Password *
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={errors.password ? 'border-danger-500' : ''}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.password}</p>
                  )}
                  <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                    Must be at least 8 characters
                  </p>
                </div>
              )}

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  First Name *
                </label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className={errors.firstName ? 'border-danger-500' : ''}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Last Name *
                </label>
                <Input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className={errors.lastName ? 'border-danger-500' : ''}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.lastName}</p>
                )}
              </div>

              {/* Phone */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              {/* Roles */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Roles
                </label>
                <RoleSelector
                  selectedRoleIds={formData.roleIds}
                  onChange={(roleIds) => setFormData({ ...formData, roleIds })}
                />
              </div>

              {/* Active Status */}
              <div className="col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-secondary-700 dark:text-secondary-300">
                    Active user account
                  </span>
                </label>
              </div>

              {/* Email Verified (only for edit) */}
              {isEditing && (
                <div className="col-span-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.emailVerified}
                      onChange={(e) => setFormData({ ...formData, emailVerified: e.target.checked })}
                      className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-secondary-700 dark:text-secondary-300">
                      Email verified
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-secondary-200 dark:border-secondary-700">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create User'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
